/**
 * Labstep
 *
 * @module models/entity-export
 * @desc Typescript export class for EntityExport
 */

import { Type } from 'class-transformer';
import { Entity } from 'labstep-web/models/entity.model';
import { Group } from 'labstep-web/models/group.model';
import { Log } from 'labstep-web/models/log.model';
import { LogEntry } from 'labstep-web/models/log-entry.model';
import { User } from 'labstep-web/models/user.model';
import { UAParser } from 'ua-parser-js';

export interface EntityExportAPIBody {
  query_entity_name?: string;
  query_parameters?: Record<string, unknown>;
  fields?: Record<string, unknown>[];
  type?: string;
  group_id?: number;
  [key: string]: unknown;
}

export class EntityExport extends Entity {
  static readonly entityName = 'entity_export' as const;

  get entityName(): string {
    return EntityExport.entityName;
  }

  static TYPE_CUSTOM = 'custom';

  static TYPE_HTML = 'html';

  static TYPE_HTML_FILE = 'html_file';

  static TYPE_PRINT = 'print';

  static TYPE_ZIP = 'zip';

  static TYPE_CSV = 'csv';

  static TYPE_FILE_DOWNLOAD = 'file_download';

  constructor(data: Partial<EntityExport> = {}) {
    super();
    Object.assign(this, data);
  }

  created_at!: string;

  type!: string;

  query_entity_name!: string;

  query_parameters!: Record<string, unknown>;

  @Type(() => Log)
  log_create!: Log;

  @Type(() => LogEntry)
  log_entry_create!: LogEntry;

  @Type(() => User)
  author!: User;

  @Type(() => Group)
  owner!: Group;

  // Only added to make typescript happy for the Create API payload group_id
  @Type(() => Group)
  group!: Group;

  get userAgentObject(): UAParser.IResult {
    if (this.log_create) {
      return this.log_create.request_trace.userAgentObject;
    }

    const parser = new UAParser();

    parser.setUA(this.log_entry_create?.client_user_agent);

    return parser.getResult();
  }

  get clientDisplayLabel(): string {
    return `${this.browser.name} ${this.printDeviceType} ${this.os.name}`;
  }

  get browser(): UAParser.IBrowser {
    return this.userAgentObject.browser;
  }

  get device(): UAParser.IDevice {
    return this.userAgentObject.device;
  }

  get os(): UAParser.IOS {
    return this.userAgentObject.os;
  }

  get printDeviceType(): string {
    const { device } = this.userAgentObject;
    return device.type || 'desktop';
  }
}
